import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { PDFDocument, degrees } from "pdf-lib";
import { Loader2, FileStack, Scissors, RotateCw, Layers, Upload } from "lucide-react";
import { toast } from "sonner";
import { absoluteUrl, getPublicTool } from "@/lib/tools-catalog";
import { buildToolSoftwareApplicationJsonLd } from "@/lib/seo/tool-structured-data";

export const Route = createFileRoute("/tools/pdf")({
  head: () => {
    const tool = getPublicTool("pdf");
    const url = absoluteUrl(tool.path);
    const title = tool.pageTitle;
    const description = tool.description;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(buildToolSoftwareApplicationJsonLd(tool)),
        },
      ],
    };
  },
  component: PdfToolkitPage,
});

type Tab = "merge" | "split" | "rotate" | "extract";

function download(bytes: Uint8Array, filename: string) {
  const blob = new Blob([bytes as BlobPart], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function PdfToolkitPage() {
  const [tab, setTab] = useState<Tab>("merge");
  return (
    <section className="container-narrow py-12">
      <p className="text-xs uppercase tracking-[0.25em] text-gold">Free tool</p>
      <h1 className="mt-2 font-display text-3xl text-navy md:text-4xl">PDF Toolkit</h1>
      <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
        All operations happen in your browser using <code>pdf-lib</code>. Nothing is uploaded.
      </p>

      <div className="mt-6 flex flex-wrap gap-2 border-b border-border pb-3">
        {(
          [
            { id: "merge", label: "Merge", icon: FileStack },
            { id: "split", label: "Split", icon: Scissors },
            { id: "rotate", label: "Rotate", icon: RotateCw },
            { id: "extract", label: "Extract pages", icon: Layers },
          ] as { id: Tab; label: string; icon: any }[]
        ).map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm ${
              tab === t.id
                ? "bg-navy text-white"
                : "border border-input bg-background text-muted-foreground"
            }`}
          >
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "merge" && <MergePanel />}
        {tab === "split" && <SplitPanel />}
        {tab === "rotate" && <RotatePanel />}
        {tab === "extract" && <ExtractPanel />}
      </div>
    </section>
  );
}

function FilePicker({
  multiple,
  files,
  onChange,
}: {
  multiple?: boolean;
  files: File[];
  onChange: (f: File[]) => void;
}) {
  return (
    <label className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-input bg-card p-6 text-center text-sm text-muted-foreground hover:border-navy">
      <Upload className="mb-2 h-6 w-6 text-navy" />
      {files.length
        ? files.map((f) => f.name).join(", ")
        : `Click to select PDF${multiple ? "s" : ""}`}
      <input
        type="file"
        accept="application/pdf"
        multiple={multiple}
        className="hidden"
        onChange={(e) => onChange(Array.from(e.target.files ?? []))}
      />
    </label>
  );
}

function MergePanel() {
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  async function merge() {
    if (files.length < 2) return toast.error("Select at least 2 PDFs");
    setBusy(true);
    try {
      const out = await PDFDocument.create();
      for (const f of files) {
        const src = await PDFDocument.load(await f.arrayBuffer(), { ignoreEncryption: true });
        const pages = await out.copyPages(src, src.getPageIndices());
        pages.forEach((p) => out.addPage(p));
      }
      download(await out.save(), "merged.pdf");
      toast.success("Merged");
    } catch (e: any) {
      toast.error(e?.message || "Merge failed");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="space-y-4">
      <FilePicker multiple files={files} onChange={setFiles} />
      <button
        onClick={merge}
        disabled={busy}
        className="inline-flex items-center gap-2 rounded-md bg-navy px-4 py-2 text-sm font-semibold text-white hover:bg-navy/90 disabled:opacity-50"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileStack className="h-4 w-4" />}
        Merge {files.length || ""} PDFs
      </button>
    </div>
  );
}

function SplitPanel() {
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  async function split() {
    if (!files[0]) return toast.error("Select a PDF");
    setBusy(true);
    try {
      const src = await PDFDocument.load(await files[0].arrayBuffer(), { ignoreEncryption: true });
      for (let i = 0; i < src.getPageCount(); i++) {
        const out = await PDFDocument.create();
        const [page] = await out.copyPages(src, [i]);
        out.addPage(page);
        download(await out.save(), `page-${i + 1}.pdf`);
      }
      toast.success(`Split into ${src.getPageCount()} files`);
    } catch (e: any) {
      toast.error(e?.message || "Split failed");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="space-y-4">
      <FilePicker files={files} onChange={setFiles} />
      <p className="text-xs text-muted-foreground">
        Downloads one PDF per page. For a large file, your browser may prompt to allow multiple
        downloads.
      </p>
      <button
        onClick={split}
        disabled={busy}
        className="inline-flex items-center gap-2 rounded-md bg-navy px-4 py-2 text-sm font-semibold text-white hover:bg-navy/90 disabled:opacity-50"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Scissors className="h-4 w-4" />}
        Split into pages
      </button>
    </div>
  );
}

function RotatePanel() {
  const [files, setFiles] = useState<File[]>([]);
  const [angle, setAngle] = useState<90 | 180 | 270>(90);
  const [busy, setBusy] = useState(false);
  async function rotate() {
    if (!files[0]) return toast.error("Select a PDF");
    setBusy(true);
    try {
      const src = await PDFDocument.load(await files[0].arrayBuffer(), { ignoreEncryption: true });
      src.getPages().forEach((p) => {
        const current = p.getRotation().angle;
        p.setRotation(degrees((current + angle) % 360));
      });
      download(await src.save(), `rotated-${angle}.pdf`);
      toast.success("Rotated");
    } catch (e: any) {
      toast.error(e?.message || "Rotate failed");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="space-y-4">
      <FilePicker files={files} onChange={setFiles} />
      <div className="flex gap-2 text-xs">
        {[90, 180, 270].map((a) => (
          <button
            key={a}
            onClick={() => setAngle(a as 90 | 180 | 270)}
            className={`rounded-full border px-3 py-1 ${
              angle === a
                ? "border-navy bg-navy text-white"
                : "border-input bg-background text-muted-foreground"
            }`}
          >
            {a}°
          </button>
        ))}
      </div>
      <button
        onClick={rotate}
        disabled={busy}
        className="inline-flex items-center gap-2 rounded-md bg-navy px-4 py-2 text-sm font-semibold text-white hover:bg-navy/90 disabled:opacity-50"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCw className="h-4 w-4" />}
        Rotate all pages
      </button>
    </div>
  );
}

function parseRanges(input: string, total: number): number[] {
  const out = new Set<number>();
  for (const part of input.split(",").map((s) => s.trim()).filter(Boolean)) {
    const m = part.match(/^(\d+)(?:\s*-\s*(\d+))?$/);
    if (!m) continue;
    const start = Math.max(1, Number(m[1]));
    const end = Math.min(total, Number(m[2] ?? m[1]));
    for (let i = start; i <= end; i++) out.add(i);
  }
  return Array.from(out).sort((a, b) => a - b);
}

function ExtractPanel() {
  const [files, setFiles] = useState<File[]>([]);
  const [ranges, setRanges] = useState("1-3, 5");
  const [busy, setBusy] = useState(false);
  async function extract() {
    if (!files[0]) return toast.error("Select a PDF");
    setBusy(true);
    try {
      const src = await PDFDocument.load(await files[0].arrayBuffer(), { ignoreEncryption: true });
      const pages = parseRanges(ranges, src.getPageCount());
      if (!pages.length) return toast.error("No valid pages in range");
      const out = await PDFDocument.create();
      const copied = await out.copyPages(src, pages.map((p) => p - 1));
      copied.forEach((p) => out.addPage(p));
      download(await out.save(), "extracted.pdf");
      toast.success(`Extracted ${pages.length} page(s)`);
    } catch (e: any) {
      toast.error(e?.message || "Extract failed");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="space-y-4">
      <FilePicker files={files} onChange={setFiles} />
      <label className="block text-sm">
        <span className="text-xs text-muted-foreground">Pages (e.g. 1-3, 5, 8-10)</span>
        <input
          value={ranges}
          onChange={(e) => setRanges(e.target.value)}
          className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        />
      </label>
      <button
        onClick={extract}
        disabled={busy}
        className="inline-flex items-center gap-2 rounded-md bg-navy px-4 py-2 text-sm font-semibold text-white hover:bg-navy/90 disabled:opacity-50"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Layers className="h-4 w-4" />}
        Extract pages
      </button>
    </div>
  );
}
